import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ImageUpload from "./ImageUpload";
import BlogCard from "../BlogCard";
import { useSEO } from "@/hooks/useSEO";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  author: {
    full_name: string | null;
    username: string | null;
  } | null;
  published_at: string;
  created_at: string;
  slug: string;
}

const BlogManager = () => {
  useSEO({
    title: "Manage Blog | Loch Ness Story",
    description: "Admin area for managing blog posts.",
    canonical: "/blog/manage",
    noindex: true,
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });
  }, []);

  const { data: blogPosts, isLoading } = useQuery({
    queryKey: ['manageBlogPosts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          author:profiles(full_name, username)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BlogPost[];
    },
  });

  const handleImageUploaded = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    toast({
      title: "Image uploaded successfully",
      description: "You can now use this image in your blog post.",
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p className="text-gray-600 text-center">
          You need to be logged in to access the blog manager.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <div>Loading blog posts...</div>;
  }

  return (
    <div className="space-y-8">
      <div className="bg-slate-800 p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4 text-white">Upload Blog Image</h2>
        <ImageUpload onImageUploaded={handleImageUploaded} />
        {selectedImage && (
          <div className="mt-4">
            <p className="text-sm text-gray-400 mb-2">Uploaded image URL:</p>
            <code className="bg-slate-900 p-2 rounded text-sm text-gray-300 block break-all">
              {selectedImage}
            </code>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {blogPosts?.map((post) => (
          <BlogCard
            key={post.id}
            title={post.title}
            excerpt={post.excerpt}
            image={post.image_url || "/placeholder.svg"}
            author={post.author?.full_name || post.author?.username || "Anonymous"}
            date={new Date(post.published_at || post.created_at).toLocaleDateString()}
            categories={["Blog"]}
            slug={post.slug}
          />
        ))}
      </div>
    </div>
  );
};

export default BlogManager;
